import type { MutableRefObject } from 'react';
import type { Editor } from '@tiptap/core';
import type { Story, Thread } from '@proofroom/schema';
import type { SnapshotIndex } from '../model/snapshotIndex';
import { StoryEditor, type StoryEditorProps } from '../editor/StoryEditor';
import type { Room } from '../editor/rooms';

export interface EditorPanelProps extends Omit<StoryEditorProps, 'editorRef'> {
  story: Story;
  idx: SnapshotIndex;
  room: Room;
  threads: Thread[];
  editorRef: MutableRefObject<Editor | null>;
  caretStyleName?: string;
  composing: boolean;
  composeError?: string;
  roomStatus: Room['status'];
  canComment: boolean;
  canBold: boolean;
  canItalic: boolean;
  onComment(): void;
  onClose(): void;
}

export function EditorPanel(p: EditorPanelProps) {
  const ed = p.editorRef.current;
  const run = (fn: (e: Editor) => void) => { const e = p.editorRef.current; if (e) fn(e); };
  const statusDot = p.roomStatus === 'disconnected' ? 'off' : p.composing ? 'busy' : '';
  const statusText = p.roomStatus === 'local' ? 'Local' : p.roomStatus === 'connected' ? 'Shared' : p.roomStatus === 'connecting' ? 'Connecting' : 'Offline';
  return (
    <>
      <div className="panel-head">
        <span>Story {p.story.id}</span>
        {p.caretStyleName && <span className="chip">{p.caretStyleName}</span>}
        <span className="grow" />
        <button className="btn sm ghost" onClick={p.onClose} title="Close editor (Escape)">Close</button>
      </div>
      <div className="editor-tools">
        <button className={`btn sm${ed?.isActive('bold') ? ' on' : ''}`} disabled={!p.canBold} onMouseDown={(e) => e.preventDefault()} onClick={() => run((e) => e.chain().focus().toggleBold().run())} title={p.canBold ? 'Bold' : 'This family has no bold face in the draft'}>B</button>
        <button className={`btn sm${ed?.isActive('italic') ? ' on' : ''}`} style={{ fontStyle: 'italic' }} disabled={!p.canItalic} onMouseDown={(e) => e.preventDefault()} onClick={() => run((e) => e.chain().focus().toggleItalic().run())} title={p.canItalic ? 'Italic' : 'This family has no italic face in the draft'}>I</button>
        <button className="btn sm ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => run((e) => e.chain().focus().undo().run())} title="Undo">Undo</button>
        <button className="btn sm ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => run((e) => e.chain().focus().redo().run())} title="Redo">Redo</button>
        <button className="btn sm" disabled={!p.canComment} onMouseDown={(e) => e.preventDefault()} onClick={p.onComment} title="Comment on the selected text">Comment</button>
        <span className="status"><span className={`dot ${statusDot}`} />{p.composing ? 'Composing' : statusText}</span>
      </div>
      {p.composeError && <div className="badge danger" style={{ margin: '6px 0' }}>{p.composeError}</div>}
      <StoryEditor {...p} />
    </>
  );
}
